import { FormFieldType, type FormField } from '@/models';
import { isAnswered } from '@/models/fieldAnswer';

/** The header ring's two numbers: "`answered` of `required` required". */
export interface RequiredProgress {
  answered: number;
  required: number;
}

/** A field a condition hides or that is archived is not drawn, so neither Save's checks nor the ring count it. */
export const isShownToSave = (field: FormField): boolean => field.show !== false && !field.archivedAt;

/**
 * A field Save refuses to go past while it is unanswered: shown, not archived and strict-required, and not a title,
 * which asks nothing. The ring counts these, and the field row draws amber on one left unanswered (ENG-9764).
 */
export const countsAsRequired = (field: FormField): boolean =>
  !!field.strictRequired && field.type !== FormFieldType.TITLE && isShownToSave(field);

/**
 * How many of the entry's required fields are answered, for the header ring (ENG-9761).
 *
 * It counts the fields Save's required check counts (`lookForRequiredFields` in the entry store): shown, not archived
 * and strict-required. A soft-required field never stops Save, so the ring leaves it out as well, and so is a title,
 * which asks nothing. Answered means `isAnswered`, so `required - answered` is how many fields Save will refuse over. A
 * section's own count, on its header row and its section list row (`sectionOutline.ts`), is this over the section's
 * fields, so the rings agree.
 *
 * The section list also rings a different set: the server's outstanding ids (docs/memory/outstanding-fields.md), which
 * the study's completion logic picks rather than the required setting. Both ask `isAnswered`, so they agree about
 * whether a field is answered, but not about which fields they count.
 */
export const requiredProgress = (fields: FormField[] | undefined): RequiredProgress => {
  const counted = (fields ?? []).filter(countsAsRequired);
  return {
    answered: counted.filter(isAnswered).length,
    required: counted.length
  };
};
